import { layerPropertiesAtom } from '@/atoms/selection.atom';
import { execute } from '@/graphql/execute';
import { layerQuery } from '@/queries/layer.query';
import { ArrowUpRight } from '@mynaui/icons-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import prettyBytes from 'pretty-bytes';
import { useMemo, type FunctionComponent } from 'react';
import { useParams } from 'react-router-dom';
import { Coords } from './Coords';
import Spinner from './Spinner';
import { Button } from './ui/button';
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from './ui/drawer';
import { Label } from './ui/label';

interface iFeaturePropertiesProps {}

const FeatureProperties: FunctionComponent<iFeaturePropertiesProps> = () => {
  const { projectId } = useParams();
  const [selectedLayer, setSelectedLayer] = useAtom(layerPropertiesAtom);
  const { data, isLoading } = useQuery({
    queryKey: ['layer', selectedLayer?.id],
    queryFn: () => execute(layerQuery, { id: selectedLayer?.id! }),
    enabled: !!selectedLayer?.id,
  });

  const _handleClose = () => {
    setSelectedLayer(null);
    setTimeout(() => {
      document.body.style.pointerEvents = 'auto';
    }, 500);
  };

  const _handleOpenAttachment = () => {
    window.open(
      `/projects/${projectId}/upload/batch/${data?.layer.sourceAttachment?.batchId}`,
      '_blank',
    );
  };

  const properties = useMemo(() => {
    if (!data?.layer) return [];

    const layer = data.layer;

    const bbox = layer.bboxNew?.coords as undefined | [[number, number, number], [number, number, number]];

    return [
      {
        label: 'ID',
        value: layer.id,
      },
      {
        label: 'Name',
        value: layer.name,
      },
      {
        label: 'Source Type',
        value: layer.metadata?.source,
      },
      layer.sourceAttachment
        ? {
            label: 'Source File',
            children: (
              <div className='font-medium text-sm font-mono'>
                <span
                  onClick={_handleOpenAttachment}
                  className='flex items-center gap-1 hover:underline cursor-pointer'>
                  {layer.sourceAttachment.filename} ({prettyBytes(layer.sourceAttachment?.contentLength ?? 0)})
                  <ArrowUpRight className='size-4' />
                </span>
                <span>Uploaded at {dayjs(layer.sourceAttachment.createdAt).format('DD/MM/YYYY HH:mm')}</span>
              </div>
            ),
            type: 'ATTACHMENT',
          }
        : null,
      bbox
        ? {
            label: 'Extent',
            children: <Coords coords={bbox} />,
            type: 'COORDS',
          }
        : null,
      layer.center
        ? {
            label: 'Center',
            children: <Coords coords={layer.center.coords} />,
          }
        : null,
      typeof layer.metadata.featureCount !== 'undefined'
        ? {
            label: 'Feature Count',
            value: layer.metadata.featureCount.toLocaleString(),
          }
        : null,
      typeof layer.metadata.imageCount !== 'undefined'
        ? {
            label: 'Image Count',
            value: layer.metadata.imageCount.toLocaleString(),
          }
        : null,
      typeof layer.metadata.potreeMetadata !== 'undefined'
        ? {
            label: 'Point Count',
            value: layer.metadata.potreeMetadata.points.toLocaleString(),
          }
        : null,
      layer.metadata.potreeMetadata
        ? {
            label: 'Attributes',
            value: (
              <ul className='list-disc list-inside pl-1'>
                {layer.metadata.potreeMetadata.attributes.map((attr: any) => (
                  <li key={attr.name} className='text-sm font-medium'>
                    {attr.name}
                  </li>
                ))}
              </ul>
            ),
          }
        : null,
      layer.metadata.potreeMetadata?.attributes?.some((a: any) => a.name === 'classification')
        ? {
            label: 'Classifications',
            children: `${layer.metadata.potreeMetadata.attributes.find((a: any) => a.name === 'classification').min[0]} – ${layer.metadata.potreeMetadata.attributes.find((a: any) => a.name === 'classification').max[0]}`,
          }
        : null,
    ].filter(Boolean);
  }, [data?.layer]);

  return (
    <Drawer
      onRelease={() => {
        document.body.style.pointerEvents = 'auto';
      }}
      open={!!selectedLayer}
      onClose={_handleClose}>
      <DrawerContent>
        <DrawerHeader className='w-lg'>
          <DrawerTitle>Layer Properties</DrawerTitle>
        </DrawerHeader>
        <div className='flex-1 overflow-y-auto max-w-160'>
          {isLoading ? (
            <div className='flex flex-col gap-4 px-4 py-2'>
              <Spinner className='size-8' />
            </div>
          ) : (
            <div className='flex flex-col gap-4 px-4 py-2'>
              {properties.map(
                (property) =>
                  property && (
                    <div key={property.label} className='flex flex-col gap-0.5'>
                      <Label className='text-xs font-medium'>{property.label}</Label>
                      {property.children ? (
                        property.children
                      ) : (
                        <span className='font-medium text-sm font-mono'>{property.value}</span>
                      )}
                    </div>
                  ),
              )}
            </div>
          )}
          <div className='flex flex-col gap-4 px-4 py-2'></div>
        </div>
        <DrawerFooter>
          <Button variant='link' onClick={_handleClose}>
            Close
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default FeatureProperties;
